import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, AlertCircle } from "lucide-react";

const signupSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  confirm_password: z.string(),
  data_consent: z.boolean().refine((val) => val === true, {
    message: "You must accept the data collection policy",
  }),
}).refine((data) => data.password === data.confirm_password, {
  message: "Passwords don't match",
  path: ["confirm_password"],
});

type SignupFormValues = z.infer<typeof signupSchema>;

const SignupFromInvitation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [invitationData, setInvitationData] = useState<any>(null);

  useEffect(() => {
    const token = searchParams.get("token");
    const storedData = sessionStorage.getItem("invitation_data");

    if (!token || !storedData) {
      navigate("/auth");
      return;
    }

    const data = JSON.parse(storedData);
    setInvitationData(data);
  }, [searchParams, navigate]);

  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      email: invitationData?.invitee_email || "",
      password: "",
      confirm_password: "",
      data_consent: false,
    },
  });

  useEffect(() => {
    if (invitationData) {
      form.setValue("email", invitationData.invitee_email);
    }
  }, [invitationData, form]);

  const onSubmit = async (values: SignupFormValues) => {
    setIsLoading(true);

    try {
      // Create Supabase auth account with minimal data
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: values.email,
        password: values.password,
        options: {
          data: {
            display_name: invitationData?.invitee_name || values.email.split('@')[0],
          },
        },
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error("Failed to create account");

      // Update the profile with data consent (trigger creates basic profile)
      const { error: profileError } = await supabase.from("profiles").update({
        data_collection_consent: values.data_consent,
        data_collection_consent_at: new Date().toISOString(),
      }).eq('id', authData.user.id);

      if (profileError) {
        console.error("Profile update error:", profileError);
      }

      // Update invitation to registered status
      const { error: invitationUpdateError } = await supabase
        .from("invitations")
        .update({
          invitation_status: "registered",
          registered_at: new Date().toISOString(),
          registered_user_id: authData.user.id,
        })
        .eq("tracking_token", searchParams.get("token"));

      if (invitationUpdateError) {
        console.error("Invitation update error:", invitationUpdateError);
      }

      // Create referral metrics entry
      const { error: metricsError } = await supabase
        .from("referral_metrics")
        .insert({
          referral_user_id: authData.user.id,
          inviter_id: invitationData.inviter_id,
          invitation_id: invitationData.id,
          channel_id: invitationData.channel_id,
          onboarding_started_at: new Date().toISOString(),
        });

      if (metricsError) {
        console.error("Metrics error:", metricsError);
      }

      // Track account creation activity
      await supabase.functions.invoke("track-user-activity", {
        body: {
          action_type: "account_created",
          action_target: "/signup-from-invitation",
          action_metadata: {
            channel: invitationData.channel_name,
            invited_by: invitationData.inviter_id,
          },
        },
      });

      toast({
        title: "Account created!",
        description: "Now let's complete your profile...",
      });

      // Keep invitation_data in session storage - CreateMySPAIProfile will use it
      // Navigate to full profile creation
      navigate("/create-myspai-profile");
    } catch (error: any) {
      console.error("Signup error:", error);
      toast({
        title: "Signup failed",
        description: error.message || "An error occurred during signup",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!invitationData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-md mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Create Your Account</CardTitle>
            <CardDescription>
              You've been invited to join {invitationData.channel_name || "iSPAI"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Alert className="mb-6 border-primary/50 bg-primary/5">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong className="block mb-2">MVP Data Collection Notice</strong>
                During our MVP phase, iSPAI tracks user engagement metrics to test and improve user
                experience. Please avoid uploading private IP or confidential content.
              </AlertDescription>
            </Alert>

            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  {...form.register("email")}
                  readOnly
                  className="bg-muted"
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive">{form.formState.errors.email.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input id="password" type="password" {...form.register("password")} />
                {form.formState.errors.password && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.password.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm Password *</Label>
                <Input id="confirm_password" type="password" {...form.register("confirm_password")} />
                {form.formState.errors.confirm_password && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.confirm_password.message}
                  </p>
                )}
              </div>

              <div className="flex items-start space-x-2">
                <Controller
                  name="data_consent"
                  control={form.control}
                  render={({ field }) => (
                    <Checkbox 
                      id="data_consent" 
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <label htmlFor="data_consent" className="text-sm leading-relaxed">
                  I acknowledge the MVP Data Collection Notice and consent to engagement tracking.
                </label>
              </div>
              {form.formState.errors.data_consent && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.data_consent.message}
                </p>
              )}

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Create Account & Continue
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SignupFromInvitation;
